import { useLeasingUrlParams } from '../../hooks/useLeasingUrlParams';

import { Button, ButtonGroup, Heading, Paragraph, TextButton } from '@zillow/constellation-10';
import { LeaseStatus } from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import { LeaseArtifactType } from '@zg-rentals/ts-leasing-client';

import WhoHasSigned from '../Widgets/WhoHasSigned';
import type { Lease } from '../../models';
import LeaseModal from '../Containers/LeaseModal';
import { CANCEL, CANCELLATION_REASON } from '../../constants/stringConstants';
import { emitYesEditLeaseEvent } from '../../../analytics/events';
import { useCancelSigningMutation, useWebAnalyticsForListingQuery } from '../../utils/queries';

type Props = {
  lease: Lease;
  onCancelEdit: () => void;
  isWizardView?: boolean;
};

export default function EditBlocker(props: Props) {
  const { user } = useRentalManagerUser();
  const { propertyId } = useLeasingUrlParams();
  const { isWizardView, lease, onCancelEdit } = props;

  const webAnalyticsQuery = useWebAnalyticsForListingQuery(propertyId, { enabled: !!propertyId });
  const zrm_listing_info = webAnalyticsQuery.data;
  const cancelSigningMutation = useCancelSigningMutation();

  async function onCancelSigningClick() {
    emitYesEditLeaseEvent({ user, listing: zrm_listing_info });

    try {
      await cancelSigningMutation.mutateAsync({
        postData: {
          leaseArtifactId: lease.id as unknown as number,
          leaseArtifactType: LeaseArtifactType.LEASE,
          message: CANCELLATION_REASON,
        },
      });
    } finally {
      onCancelEdit();
    }
  }

  function handleCancel() {
    onCancelEdit();
  }

  function handleClose() {
    if (!cancelSigningMutation.isPending) {
      onCancelEdit();
    }
  }

  if (!lease) {
    return null;
  }

  function renderEditBlockerContent() {
    const isOfflineSigning = lease.status === LeaseStatus.SENT_FOR_OFFLINE_SIGNING;
    const offlineMessage =
      'After you make your edits, you’ll need to start a new signing by requesting signatures again.';
    const onlineMessage =
      'Making changes will pause the signing and cancel any existing signatures. After you make your edits, you’ll need to start a new signing by requesting signatures again.';

    return (
      <>
        <Paragraph css={{ marginBottom: 'tight' }}>{isOfflineSigning ? offlineMessage : onlineMessage}</Paragraph>
        {lease.showWhoHasSigned() && <WhoHasSigned isInitiallyExpanded leaseId={lease.id} />}
      </>
    );
  }

  // When it is in wizard view, return the full modal, when not return only the content for the sectionwrapper modal.
  return isWizardView ? (
    <LeaseModal
      header={<Heading level={1}>Edit your lease?</Heading>}
      id="edit-blocker"
      onClose={handleClose}
      isOpen
      body={renderEditBlockerContent()}
      footer={
        <ButtonGroup aria-label="make edits">
          <TextButton onClick={handleCancel} disabled={cancelSigningMutation.isPending} data-test="go-back">
            {CANCEL}
          </TextButton>
          <Button
            tone="brand"
            emphasis="filled"
            loading={cancelSigningMutation.isPending}
            onClick={onCancelSigningClick}
          >
            Yes
          </Button>
        </ButtonGroup>
      }
    />
  ) : (
    renderEditBlockerContent()
  );
}
